// @flow

import * as React from 'react';
import { Flex, Box, useSystem } from 'react-system';
import { graphql, useFragment } from 'react-relay';
import { Card, CardActionArea, Button } from '@material-ui/core';
import { Link } from '../../controls/link';
import { FixedPagination } from '../../controls/FixedPagination';
import { useLocale } from '../../controls/locale';
import { useTheme } from '../../theme';
import { Home } from '../../icons/home.js';
import { Cancel } from '../../icons/cancel.js';
import { useAnalytics } from '../../controls/analytics';
import { getIdFromGlobalId } from '../../utils/global-id';
import { useScrollTopOnRouteChangeTimeout } from '../../hooks/scroll_top_on_route_change_timeout';
import { InlineMarked } from '../../controls/marked';
import { getListingDetailUrl } from '../../utils/listing-url.js';
import {
  AggregatesListingCard,
  useGridLoadingStyle,
} from './AggregatesListingCard';
import {
  offerTypeDescriptor,
  propertyTypeFilterDescriptor,
} from './Filters/sharedDescriptors';
import { listingsDataDescriptor } from './listingsDescriptor';
import { useActions } from './state';
import { APPRAISABLE_PROPERTY_TYPES } from './consts';
import type { AggregatesListingsItems_aggregatesListings$key } from './__generated__/AggregatesListingsItems_aggregatesListings.graphql';

type Props = {|
  aggregatesListings: ?AggregatesListingsItems_aggregatesListings$key,
  page: number,
  pagesCount: number,
  totalCount: number,
  onPageChange: number => void,
  hasSimilarItems: boolean,
|};

const ResultsPagination = ({ count, page, visibleCount }) => {
  const { t, numberLocale } = useLocale();
  const { text, colors } = useTheme();

  const value = numberLocale.format(count, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });

  const from = page >= 0 ? page * 24 + 1 : 0;
  const to = visibleCount > 0 ? from - 1 + visibleCount : 0;

  return (
    <Box
      pb={[4, 3]}
      css={[text.caption, { color: colors.grey500, textAlign: 'center' }]}
    >
      {t('nResultsPagination', { value, count, from, to })}
    </Box>
  );
};

const AppraisalCard = () => {
  const { text, colors } = useTheme();
  const { media } = useSystem();
  const { t, language } = useLocale();
  const { track } = useAnalytics();
  return (
    <Link href={{ pathname: '/appraisal' }}>
      <CardActionArea css={{ height: '100%' }} target="_blank">
        <Card
          elevation={4}
          className="imageLoadingInvisibleClassname"
          css={{ cursor: 'pointer', height: '100%' }}
          onClick={() =>
            track('RA.Web.Listing.ValuationCardClick', {
              category: 'Click',
              label: 'New valuation card click',
              language,
            })
          }
        >
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            css={media({
              height: '100%',
              background: `url(/static/images/appraisal-card-${language}.svg)`,
              backgroundPosition: 'right bottom',
              backgroundRepeat: 'no-repeat',
              backgroundSize: ['55%', '75%'],
              backgroundColor: colors.background.card,
              minHeight: '270px',
            })}
          >
            <Box p={3}>
              <Box
                css={[
                  text.h5,
                  {
                    color: colors.primaryMain,
                  },
                ]}
              >
                <InlineMarked>{t('howMuchIsYourHomeWorth')}</InlineMarked>
              </Box>
              <Box
                pt={2}
                css={[
                  text.subtitle1,
                  {
                    color: colors.highText,
                  },
                ]}
              >
                {t('appraisalTime')}
              </Box>
            </Box>
            <Box mt={3} p={3}>
              <Button variant="contained" color="primary" size="small">
                {t('estimateNow')}
              </Button>
            </Box>
          </Flex>
        </Card>
      </CardActionArea>
    </Link>
  );
};

const insertApparaisalCards = pageItems => {
  const modifiedPageItems = [];
  const itemsCount = pageItems.length;

  if (itemsCount > 0 && itemsCount <= 5) {
    modifiedPageItems.push(...pageItems, { node: 'appraisal-card' });
    return modifiedPageItems;
  }

  if (itemsCount > 5) {
    modifiedPageItems.push(
      ...pageItems.slice(0, 5),
      {
        node: 'appraisal-card',
      },
      ...pageItems.slice(5, itemsCount),
    );
  }

  return modifiedPageItems;
};

export const AggregatesListingsItems = (props: Props): React.Node => {
  const { t } = useLocale();
  const { text, colors } = useTheme();

  const aggregatesListings = useFragment(
    graphql`
      fragment AggregatesListingsItems_aggregatesListings on AggregatesListingConnectionConnection
      @argumentDefinitions(
        filters: { type: "AggregatesListingFiltersInput", defaultValue: null }
      ) {
        edges {
          node {
            id
            title
            slug
            propertyMainType
            propertyType
            compositePropertyType
            portal
            offerType
            salePrice
            grossRentMonthly
            postcode
            subLocality
            locality
            lat
            lng
            computedSurface
            numberOfRooms
            numberOfBathrooms
            numberOfParking
            constructionYear
            renovationYear
            agencyId
            agencyName
            ...AggregatesListingCard_listing @arguments(filters: $filters)
          }
        }
      }
    `,
    props.aggregatesListings,
  );
  const { page, pagesCount, onPageChange, totalCount } = props;

  const loadingGridStyle = useGridLoadingStyle();
  const scrollToTopTimeout = useScrollTopOnRouteChangeTimeout();
  const [{ offerType }] = offerTypeDescriptor.useState();
  const [{ compositePropertyType_eq: propertyType }] =
    propertyTypeFilterDescriptor.useState();
  const pageItems = aggregatesListings?.edges ?? [];
  const changing = listingsDataDescriptor.useHasChanged();
  const { onListingCardShowPopup, onListingCardHidePopup } = useActions();

  // Track listings views
  React.useEffect(() => {
    const edges = aggregatesListings?.edges ?? [];
    const listings = edges.map(edge => edge?.node).filter(Boolean);

    if (listings.length > 0) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'CHF',
          item_list_id: 'search_results',
          items: listings.map((listing, index) => ({
            item_id: getIdFromGlobalId(listing.id),
            item_name: listing.title,
            index,
            item_category: listing.offerType,
            item_category2: listing.propertyMainType,
            item_category3: listing.propertyType,
            price:
              listing.offerType === 'buy'
                ? listing.salePrice
                : listing.grossRentMonthly,
            url: [
              window.location.origin,
              getListingDetailUrl(
                listing.offerType,
                listing.compositePropertyType,
                listing.slug,
              ),
            ].join(''),
            portal: listing.portal,
            postcode: listing.postcode,
            locality: listing.subLocality,
            municipality: listing.locality,
            lat: listing.lat,
            lng: listing.lng,
            computed_surface: listing.computedSurface,
            number_of_rooms: listing.numberOfRooms,
            number_of_bathrooms: listing.numberOfBathrooms,
            number_of_parkings: listing.numberOfParking,
            construction_year: listing.constructionYear,
            renovation_year: listing.renovationYear,
            agency_id: listing.agencyId,
            agency_name: listing.agencyName,
          })),
        },
      });
    }
  }, [aggregatesListings?.edges]);

  const modifiedPageItems =
    offerType === 'buy' && APPRAISABLE_PROPERTY_TYPES.includes(propertyType)
      ? insertApparaisalCards(pageItems)
      : pageItems;

  return (
    <>
      {modifiedPageItems.length > 0 && (
        <Box
          pt={3}
          pb={[3, 4]}
          css={[
            changing && loadingGridStyle,
            {
              // Trick to not use grid on ios safari 10 as its buggy,
              // and fallback there on display: block
              // explanation: scroll-snap-type: x mandatory came to safari 11 only
              '& > div': {
                marginBottom: 16,
              },

              '@supports (scroll-snap-type: x mandatory)': {
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(240px, 1fr))`,
                gridGap: '16px',
                alignContent: 'start',
                gridAutoRows: '1fr',
                '& > div': {
                  marginBottom: 'unset',
                },
              },
            },
          ]}
        >
          {modifiedPageItems.map((edge, i) => {
            if (edge == null || edge.node == null) {
              return null;
            }
            if (edge.node === 'appraisal-card') {
              return <AppraisalCard key={i} />;
            }
            // reuse keys on page change
            return (
              <AggregatesListingCard
                key={i}
                listing={edge.node}
                offerType={offerType}
                titleComponent="h2"
                withStructuredData={true}
                onListingCardShowPopup={onListingCardShowPopup}
                onListingCardHidePopup={onListingCardHidePopup}
                shallowLink={true}
                replaceLink={false}
              />
            );
          })}
        </Box>
      )}
      {pagesCount > 1 ? (
        <FixedPagination
          page={page}
          count={pagesCount}
          onPageChange={pageNum => {
            onPageChange(pageNum);
            scrollToTopTimeout();
          }}
        />
      ) : null}
      {props.totalCount > 0 && (
        <ResultsPagination
          visibleCount={pageItems.length}
          page={page}
          count={totalCount}
        />
      )}
      {props.totalCount === 0 && (
        <Flex flexDirection="column" alignItems="center" p={3}>
          <div css={{ position: 'relative' }}>
            <Cancel
              size={18}
              css={{
                color: colors.grey300,
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            />
            <Home size={64} css={{ color: colors.grey300 }} />
          </div>
          <Box p={1} css={text.subtitle2}>
            {t('noPropertiesMatchingYourSearchWereFound')}
          </Box>
          {props.hasSimilarItems && (
            <Box p={1} css={text.caption}>
              {t('similarPropertiesMayInterestYou')}
            </Box>
          )}
        </Flex>
      )}
    </>
  );
};
